import PlaceIcon from '@mui/icons-material/Place';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { User } from '../userTypes';

const MAX_VISIBLE_NODES = 1;

function perimeterLabel(
  node: User['perimeter_nodes'][number],
  t: (key: string) => string,
): string | null {
  if (node.type === 'all') return t('users:perimeter.all');
  return node.code;
}

export function PerimeterBadges({ user }: { user: User }) {
  const { t } = useTranslation('users');
  const theme = useTheme();

  const items = [
    ...new Map(
      user.perimeter_nodes.flatMap(node => {
        const label = perimeterLabel(node, t);
        const key = `${node.type}:${node.code ?? ''}`;
        return label ? [[key, { key, label }] as const] : [];
      }),
    ).values(),
  ];
  if (items.length === 0) return null;
  const visible = items.slice(0, MAX_VISIBLE_NODES);
  const hidden = items.slice(MAX_VISIBLE_NODES);

  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    height: 26,
    px: '12px',
    borderRadius: theme.borderRadius.pill,
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
  } as const;

  const labelSx = {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: '0.01em',
    lineHeight: 1,
    color: theme.palette.primary.main,
  } as const;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '6px', alignItems: 'center' }}>
      {visible.map(({ key, label }) => (
        <Box key={key} sx={chipSx}>
          <PlaceIcon
            sx={{
              fontSize: theme.typography.caption.fontSize,
              color: theme.palette.primary.main,
              opacity: 0.8,
            }}
          />
          <Typography sx={labelSx}>{label}</Typography>
        </Box>
      ))}

      {hidden.length > 0 && (
        <Tooltip title={hidden.map(item => item.label).join(', ')} placement="top" describeChild>
          <Box
            component="span"
            tabIndex={0}
            aria-label={`+${hidden.length}`}
            sx={{ ...chipSx, cursor: 'default' }}
          >
            <Typography sx={labelSx}>+{hidden.length}</Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
